import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import {
  X,
  Star,
  MapPin,
  Phone,
  Globe,
  Clock,
  Navigation,
  Crown,
  ShieldCheck,
  Wine,
  Loader2,
  ChevronLeft,
  ChevronRight,
  Quote,
  Flag,
  Share2,
  BadgeCheck,
} from "lucide-react";
import { fetchPlaceDetails } from "@/lib/api";
import { getRestaurantImage } from "@/lib/images";

const HALAL_LABELS = {
  certified: "Halal Certified",
  self_declared: "Self-declared Halal",
  muslim_owned: "Muslim-owned",
};

export default function RestaurantDetailsSheet({
  open,
  onOpenChange,
  restaurant,
  onReport,
  onClaim,
}) {
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [photoIdx, setPhotoIdx] = useState(0);

  useEffect(() => {
    if (!open || !restaurant?.place_id) return;
    setDetails(null);
    setPhotoIdx(0);
    setLoading(true);
    let cancelled = false;
    fetchPlaceDetails(restaurant.place_id)
      .then((d) => {
        if (!cancelled) setDetails(d);
      })
      .catch((e) => {
        console.error(e);
        if (!cancelled) setDetails({ ...restaurant, photos: [] });
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, restaurant?.place_id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!restaurant) return null;

  // Merge: prefer details (full data) but fall back to the nearby restaurant info
  const data = details || restaurant;
  // Owner photos first (stack), then Google's, then fallback.
  const ownerPhotoUrls = (data.owner_photos || []).map(
    (rel) => `${process.env.REACT_APP_BACKEND_URL}${rel}`
  );
  const googlePhotos = data.photos && data.photos.length > 0
    ? data.photos.map((p) => p.url)
    : [];
  const photos = (ownerPhotoUrls.length || googlePhotos.length)
    ? [...ownerPhotoUrls, ...googlePhotos]
    : [getRestaurantImage(data)];

  const directionsUrl =
    data.google_maps_uri ||
    `https://www.google.com/maps/dir/?api=1&destination=${data.lat},${data.lng}&destination_place_id=${data.place_id}`;

  const nextPhoto = () => setPhotoIdx((i) => (i + 1) % photos.length);
  const prevPhoto = () =>
    setPhotoIdx((i) => (i - 1 + photos.length) % photos.length);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        data-testid="restaurant-details-sheet"
        className="w-full sm:max-w-[520px] p-0 overflow-hidden bg-white border-l border-[#E5E1D8] flex flex-col"
      >
        <SheetHeader className="sr-only">
          <SheetTitle>{data.name}</SheetTitle>
          <SheetDescription>Restaurant details</SheetDescription>
        </SheetHeader>

        {/* Hero photo */}
        <div className="relative h-64 sm:h-72 bg-[#F2EDE3] shrink-0 group overflow-hidden select-none">
          <AnimatePresence mode="wait" custom={photoIdx}>
            <motion.img
              key={photoIdx}
              src={photos[photoIdx]}
              alt={data.name}
              draggable={false}
              initial={{ opacity: 0, scale: 1.02 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 1.02 }}
              transition={{ duration: 0.25 }}
              drag={photos.length > 1 ? "x" : false}
              dragConstraints={{ left: 0, right: 0 }}
              dragElastic={0.35}
              onDragEnd={(e, info) => {
                if (photos.length <= 1) return;
                const threshold = 60;
                if (info.offset.x < -threshold || info.velocity.x < -350) {
                  nextPhoto();
                } else if (info.offset.x > threshold || info.velocity.x > 350) {
                  prevPhoto();
                }
              }}
              className="absolute inset-0 w-full h-full object-cover cursor-grab active:cursor-grabbing"
            />
          </AnimatePresence>
          <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-transparent to-transparent pointer-events-none" />

          {/* Close */}
          <button
            data-testid="close-details-sheet"
            onClick={() => onOpenChange(false)}
            className="absolute top-4 right-4 w-9 h-9 rounded-full bg-white/95 backdrop-blur flex items-center justify-center hover:bg-white"
          >
            <X className="w-4 h-4 text-[#2A3B2C]" />
          </button>

          {/* Photo nav */}
          {photos.length > 1 && (
            <>
              <button
                onClick={prevPhoto}
                className="absolute left-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white/90 hover:bg-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                aria-label="Previous photo"
              >
                <ChevronLeft className="w-4 h-4 text-[#2A3B2C]" />
              </button>
              <button
                onClick={nextPhoto}
                className="absolute right-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white/90 hover:bg-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                aria-label="Next photo"
              >
                <ChevronRight className="w-4 h-4 text-[#2A3B2C]" />
              </button>
              <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
                {photos.map((p, i) => (
                  <span
                    key={p.url || `photo-${i}`}
                    className={`h-1.5 rounded-full transition-all ${
                      i === photoIdx
                        ? "w-6 bg-white"
                        : "w-1.5 bg-white/60"
                    }`}
                  />
                ))}
              </div>
            </>
          )}

          {/* Badges */}
          <div className="absolute top-4 left-4 flex flex-col gap-2">
            {data.featured && (
              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider bg-gradient-to-r from-[#E1A624] to-[#D95D39] text-white shadow">
                <Crown className="w-3 h-3" />
                Featured
              </span>
            )}
            {data.badge_verified && (
              <span
                data-testid="badge-verified-trust"
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider bg-[#2E7D5B] text-white shadow"
                title="Halal Verified · owner-paid trust badge"
              >
                <ShieldCheck className="w-3 h-3" />
                Halal Verified
              </span>
            )}
            {!data.badge_verified && data.halal_verified && (
              <span
                data-testid="halal-verified-badge"
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider bg-[#2A3B2C] text-white shadow"
              >
                <ShieldCheck className="w-3 h-3" />
                {HALAL_LABELS[data.halal_status] || "Halal Verified"}
              </span>
            )}
          </div>

          {/* Title */}
          <div className="absolute bottom-4 left-5 right-5 text-white">
            <h2
              data-testid="details-title"
              className="font-display text-2xl sm:text-3xl font-medium leading-tight drop-shadow"
            >
              {data.name}
            </h2>
            <div className="mt-1 flex items-center flex-wrap gap-3 text-xs text-white/90">
              {data.rating != null && (
                <span className="inline-flex items-center gap-1 font-semibold">
                  <Star className="w-3.5 h-3.5 fill-[#E1A624] text-[#E1A624]" />
                  {data.rating.toFixed(1)}
                  {data.user_rating_count != null && (
                    <span className="font-normal opacity-80">
                      ({data.user_rating_count})
                    </span>
                  )}
                </span>
              )}
              {data.distance_m != null && (
                <span>
                  {data.distance_m < 1000
                    ? `${Math.round(data.distance_m)} m away`
                    : `${(data.distance_m / 1000).toFixed(1)} km away`}
                </span>
              )}
              {data.open_now != null && (
                <span
                  className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                    data.open_now
                      ? "bg-white text-[#2A3B2C]"
                      : "bg-white/20 text-white"
                  }`}
                >
                  {data.open_now ? "Open now" : "Closed"}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto brand-scroll px-6 pb-6 pt-5 space-y-5">
          {/* Top actions: Claim + Report (lifted out of the bottom footer
              because the Emergent badge overlaps that area on the preview). */}
          <div className="flex items-center justify-between gap-2 -mt-1">
            {onClaim ? (
              <button
                type="button"
                data-testid="details-claim-btn"
                onClick={onClaim}
                className="inline-flex items-center gap-1.5 h-8 px-3 rounded-full bg-[#2A3B2C] text-white text-xs font-medium hover:bg-[#1f2b21] transition-colors"
                title="Own this restaurant? Verify your ownership."
              >
                <ShieldCheck className="w-3.5 h-3.5" />
                Claim this restaurant
              </button>
            ) : (
              <span />
            )}
            {onReport && (
              <button
                type="button"
                data-testid="details-report-btn"
                onClick={onReport}
                className="inline-flex items-center gap-1 h-8 px-3 rounded-full bg-white border border-[#E5E1D8] text-[#647466] text-xs font-medium hover:border-[#D95D39] hover:text-[#D95D39] transition-colors"
                title="Report incorrect or non-halal info"
              >
                <Flag className="w-3 h-3" />
                Looks off?
              </button>
            )}
            <ShareSpotButton restaurant={data} />
          </div>

          {loading && !details && (
            <div className="flex items-center gap-2 text-sm text-[#647466]">
              <Loader2 className="w-4 h-4 animate-spin text-[#D95D39]" />
              Loading details from Google Maps…
            </div>
          )}

          {/* Editorial summary */}
          {data.editorial_summary && (
            <p className="text-sm leading-relaxed text-[#647466] italic">
              {data.editorial_summary}
            </p>
          )}

          {/* Owner description (overrides editorial) */}
          {data.owner_description && (
            <p
              data-testid="owner-description"
              className="text-sm leading-relaxed text-[#2A3B2C] whitespace-pre-line"
            >
              {data.owner_description}
            </p>
          )}

          {/* Menu files from owner */}
          {data.menus && data.menus.length > 0 && (
            <section data-testid="menus-section">
              <h4 className="text-xs uppercase tracking-[0.18em] font-bold text-[#647466] mb-2">
                Menu
              </h4>
              <ul className="space-y-2">
                {data.menus.map((m) => (
                  <li key={m.file_id}>
                    <a
                      href={`${process.env.REACT_APP_BACKEND_URL}${m.url}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-between gap-2 rounded-lg border border-[#E5E1D8] px-3 py-2 text-sm hover:border-[#D95D39] text-[#2A3B2C]"
                    >
                      <span className="truncate">{m.filename}</span>
                      <span className="text-[#D95D39] text-xs inline-flex items-center gap-1 shrink-0">
                        View
                      </span>
                    </a>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {/* Halal verification details */}
          {data.halal_verified && (
            <section
              data-testid="halal-info-section"
              className="rounded-2xl border border-[#2A3B2C]/15 bg-[#2A3B2C]/[0.03] p-4 space-y-2"
            >
              <div className="flex items-center gap-2">
                <ShieldCheck className="w-4 h-4 text-[#2A3B2C]" />
                <h4 className="font-display font-medium text-sm text-[#2A3B2C]">
                  Halal verification by owner
                </h4>
              </div>
              <div className="text-sm text-[#647466] space-y-1">
                <div>
                  <span className="text-[10px] uppercase tracking-wider font-bold text-[#8A9A8B] mr-2">
                    Status
                  </span>
                  <span className="font-medium text-[#2A3B2C]">
                    {HALAL_LABELS[data.halal_status] || data.halal_status}
                  </span>
                </div>
                {data.halal_certification_body && (
                  <div>
                    <span className="text-[10px] uppercase tracking-wider font-bold text-[#8A9A8B] mr-2">
                      Certifier
                    </span>
                    <span>{data.halal_certification_body}</span>
                    {data.halal_certification_number && (
                      <span className="text-[#8A9A8B]"> · #{data.halal_certification_number}</span>
                    )}
                  </div>
                )}
                {data.halal_has_alcohol != null && (
                  <div className="inline-flex items-center gap-1.5 mt-1">
                    <Wine className="w-3.5 h-3.5 text-[#8A9A8B]" />
                    <span>
                      {data.halal_has_alcohol
                        ? "Alcohol served on premises"
                        : "No alcohol served"}
                    </span>
                  </div>
                )}
                {/* Halal preparation attributes (iter19) — only render when
                    the owner has set at least one, so we don't bake-in
                    assumptions about places that haven't been claimed. */}
                {(data.halal_hand_slaughtered ||
                  data.halal_separate_cooking_area ||
                  data.halal_on_request) && (
                  <ul
                    data-testid="halal-prep-list"
                    className="mt-2 space-y-1 text-sm text-[#2A3B2C]"
                  >
                    {data.halal_hand_slaughtered && (
                      <li className="flex items-center gap-2">
                        <BadgeCheck className="w-3.5 h-3.5 text-[#2E7D5B]" />
                        Meat is hand slaughtered (zabihah)
                      </li>
                    )}
                    {data.halal_separate_cooking_area && (
                      <li className="flex items-center gap-2">
                        <BadgeCheck className="w-3.5 h-3.5 text-[#2E7D5B]" />
                        Separate cooking area for halal meat
                      </li>
                    )}
                    {data.halal_on_request && (
                      <li className="flex items-center gap-2">
                        <BadgeCheck className="w-3.5 h-3.5 text-[#2E7D5B]" />
                        Halal meat available on request
                      </li>
                    )}
                  </ul>
                )}
              </div>
            </section>
          )}

          {/* Contact */}
          <section className="space-y-2">
            {data.address && (
              <InfoRow
                icon={<MapPin className="w-4 h-4 text-[#D95D39]" />}
                label="Address"
                value={data.address}
              />
            )}
            {data.international_phone && (
              <InfoRow
                icon={<Phone className="w-4 h-4 text-[#D95D39]" />}
                label="Phone"
                value={
                  <a
                    href={`tel:${data.international_phone}`}
                    className="text-[#2A3B2C] hover:text-[#D95D39]"
                  >
                    {data.international_phone}
                  </a>
                }
              />
            )}
            {data.website && (
              <InfoRow
                icon={<Globe className="w-4 h-4 text-[#D95D39]" />}
                label="Website"
                value={
                  <a
                    href={data.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#2A3B2C] hover:text-[#D95D39] break-all"
                  >
                    {data.website.replace(/^https?:\/\//, "")}
                  </a>
                }
              />
            )}
          </section>

          {/* Hours */}
          {data.weekday_descriptions && data.weekday_descriptions.length > 0 && (
            <section>
              <div className="flex items-center gap-2 mb-2">
                <Clock className="w-4 h-4 text-[#D95D39]" />
                <h4 className="font-display font-medium text-sm text-[#2A3B2C]">
                  Opening hours
                </h4>
              </div>
              <ul className="space-y-1 text-sm text-[#647466]">
                {data.weekday_descriptions.map((d) => (
                  <li
                    key={d}
                    className="flex justify-between gap-3 border-b border-[#E5E1D8]/60 pb-1 last:border-0"
                  >
                    <span className="text-[#2A3B2C] font-medium min-w-[80px]">
                      {d.split(":")[0]}
                    </span>
                    <span className="text-right">
                      {d.split(":").slice(1).join(":").trim()}
                    </span>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {/* Reviews */}
          {data.reviews && data.reviews.length > 0 && (
            <section>
              <h4 className="font-display font-medium text-sm text-[#2A3B2C] mb-3">
                Recent reviews
              </h4>
              <div className="space-y-3">
                {data.reviews.map((r, i) => (
                  <div
                    key={`${r.author || "anon"}-${r.relative_time || i}`}
                    className="rounded-2xl border border-[#E5E1D8] bg-[#FDFBF7] p-3"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2">
                        {r.profile_photo ? (
                          <img
                            src={r.profile_photo}
                            alt={r.author}
                            className="w-7 h-7 rounded-full"
                          />
                        ) : (
                          <div className="w-7 h-7 rounded-full bg-[#D95D39]/10 flex items-center justify-center text-[10px] font-bold text-[#D95D39]">
                            {(r.author || "?").slice(0, 1)}
                          </div>
                        )}
                        <div className="text-xs">
                          <div className="font-semibold text-[#2A3B2C]">
                            {r.author || "Anonymous"}
                          </div>
                          {r.relative_time && (
                            <div className="text-[#8A9A8B]">
                              {r.relative_time}
                            </div>
                          )}
                        </div>
                      </div>
                      {r.rating != null && (
                        <span className="inline-flex items-center gap-0.5 text-xs font-semibold text-[#2A3B2C]">
                          <Star className="w-3 h-3 fill-[#E1A624] text-[#E1A624]" />
                          {r.rating.toFixed(1)}
                        </span>
                      )}
                    </div>
                    {r.text && (
                      <p className="mt-2 text-xs leading-relaxed text-[#647466]">
                        <Quote className="w-3 h-3 text-[#D95D39] inline mr-1 -mt-0.5" />
                        {r.text.length > 240
                          ? r.text.slice(0, 240) + "…"
                          : r.text}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </section>
          )}
        </div>

        {/* Sticky footer CTA — single Get Directions button. Report + Claim
            actions live in the top of the body to avoid the Emergent badge
            overlap on the bottom-right of the preview. Extra bottom padding
            keeps the button comfortably above that badge area. */}
        <div className="border-t border-[#E5E1D8] p-4 pb-6 bg-white">
          <a
            href={directionsUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="details-directions-btn"
            className="inline-flex w-full items-center justify-center gap-2 h-12 rounded-full bg-[#D95D39] hover:bg-[#C54E2C] text-white font-medium transition-colors"
          >
            <Navigation className="w-4 h-4" />
            Get Directions
          </a>
        </div>
      </SheetContent>
    </Sheet>
  );
}

function InfoRow({ icon, label, value }) {
  return (
    <div className="flex items-start gap-3">
      <div className="w-8 h-8 rounded-full bg-[#FFF3EE] flex items-center justify-center shrink-0">
        {icon}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[10px] uppercase tracking-wider font-bold text-[#8A9A8B]">
          {label}
        </div>
        <div className="text-sm text-[#2A3B2C] break-words">{value}</div>
      </div>
    </div>
  );
}

/**
 * ShareSpotButton — one-tap share that pipes through our /api/og/r/<pid>
 * URL so WhatsApp/Twitter/Slack get a rich preview card with the
 * restaurant photo + name, not a generic AlKoncierge tile.
 *
 * Falls back to clipboard copy on browsers that don't support the Web
 * Share API.
 */
function ShareSpotButton({ restaurant }) {
  if (!restaurant?.place_id) return null;
  const shareUrl = `${window.location.origin}/api/og/r/${restaurant.place_id}`;
  const onShare = async () => {
    try {
      // Track the intent — useful to see which restaurants are most-shared.
      const { track, EVENTS } = await import("@/lib/analytics");
      track(EVENTS.RESTAURANT_SHARE, { place_id: restaurant.place_id });

      if (navigator.share) {
        await navigator.share({
          title: `${restaurant.name || "Halal spot"} — AlKoncierge`,
          text: `Found this halal spot on AlKoncierge — ${
            restaurant.name || "check it out"
          }`,
          url: shareUrl,
        });
        return;
      }
      // Fallback: copy to clipboard.
      await navigator.clipboard.writeText(shareUrl);
      const { toast } = await import("sonner");
      toast.success("Link copied to clipboard");
    } catch (err) {
      // User cancelled the share sheet — that's fine, no error UX needed.
      if (err?.name === "AbortError") return;
      console.warn("[share] failed", err?.message);
    }
  };
  return (
    <button
      type="button"
      data-testid="details-share-btn"
      onClick={onShare}
      className="inline-flex items-center gap-1 h-8 px-3 rounded-full bg-white border border-[#E5E1D8] text-[#647466] text-xs font-medium hover:border-[#2A3B2C] hover:text-[#2A3B2C] transition-colors"
      title="Share this spot"
    >
      <Share2 className="w-3 h-3" />
      Share
    </button>
  );
}

